import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Loader2, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/pos/ErrorState';
import { fetchMyPermissions, logoutSession } from '@/api/auth.api';
import { useAuthStore } from '@/stores/auth.store';
import { canOperatePos } from '@/lib/permissions';
import type { ApiError } from '@/types/api';

/**
 * Lets an operator into the POS by PERMISSION — `sale.ticketbox.operate` or
 * the superadmin `*` — never by role name (a role list misses new roles such as
 * cashier_supervisor and admits ones that must not transact).
 *
 * - Permissions are persisted with the session, so a reload (or an offline
 *   reboot) renders straight away; they are re-checked against the server in
 *   the background once per mount, so a revoked grant takes effect on reload.
 * - A session persisted before permissions were stored has none: fetch them
 *   first (spinner), with retry / sign-out if that fails.
 * - Refused: revoke the session on the server, then back to /login with a
 *   reason the login page shows.
 */
export function RoleGate() {
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.permissions);
  const wildcards = useAuthStore((s) => s.wildcards);
  const setPermissions = useAuthStore((s) => s.setPermissions);
  const logout = useAuthStore((s) => s.logout);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const denying = useRef(false);

  const allowed = canOperatePos({ permissions, wildcards });

  const deny = useCallback(async () => {
    if (denying.current) return;
    denying.current = true;
    await logoutSession(useAuthStore.getState().refreshToken);
    // Navigate before clearing the store: once logged out, RequireAuth would
    // redirect on its own and drop the reason.
    navigate('/login', { replace: true, state: { error: 'PERMISSION_DENIED' } });
    logout();
  }, [navigate, logout]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    fetchMyPermissions()
      .then((p) => {
        if (cancelled) return;
        setPermissions(p);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        // With cached permissions a failed re-check keeps the till working
        // (offline LAN blip); only a session with nothing cached is blocked.
        if (useAuthStore.getState().permissions === null) {
          setLoadError(
            err?.isNetwork
              ? 'Tidak dapat terhubung ke server untuk memeriksa hak akses.'
              : (err?.message ?? 'Gagal memuat hak akses akun.'),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, setPermissions]);

  useEffect(() => {
    if (permissions !== null && !allowed) void deny();
  }, [permissions, allowed, deny]);

  if (permissions === null) {
    if (loadError) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-background p-6">
          <div className="w-full max-w-md space-y-3">
            <ErrorState
              title="Hak akses belum dimuat"
              message={loadError}
              onRetry={() => setAttempt((a) => a + 1)}
            />
            <Button
              variant="ghost"
              className="w-full gap-2"
              onClick={async () => {
                await logoutSession(useAuthStore.getState().refreshToken);
                logout();
              }}
            >
              <LogOut className="h-4 w-4" /> Keluar
            </Button>
          </div>
        </div>
      );
    }
    return <GateSpinner />;
  }

  // Refused: `deny` (effect above) revokes, navigates to /login and clears.
  if (!allowed) return <GateSpinner />;
  return <Outlet />;
}

function GateSpinner() {
  return (
    <div
      className="flex h-screen w-screen items-center justify-center bg-background text-ink-400"
      role="status"
      aria-label="Memeriksa hak akses"
    >
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  );
}
